import { useContext } from "react";
import {
  Modal,
  Group,
  Stack,
  Text,
  Title,
  Box,
  ThemeIcon,
  Button,
} from "@mantine/core";
import { IconCheck, IconX, IconArrowNarrowRight } from "@tabler/icons-react";
import { AuthContext } from "../contexts/AuthContext";
import MicrosoftLogo from "./MicrosoftLogo";
import vaparLogo from "../assets/images/vapar-logo-original.svg";
import { showErrorNotification } from "../utils/notifications";

const LOGO_BOX = 56;
const BADGE = 36;

// One account in the "Microsoft → VAPAR" pairing: a framed logo, a label and
// the account email.
const AccountBlock = ({ logo, label, email }) => (
  <Stack align="center" gap={6} style={{ flex: 1, minWidth: 0 }}>
    <Box
      style={{
        width: LOGO_BOX,
        height: LOGO_BOX,
        borderRadius: 12,
        border: "1px solid var(--mantine-color-default-border)",
        backgroundColor: "var(--mantine-color-body)",
        display: "grid",
        placeItems: "center",
      }}
    >
      {logo}
    </Box>
    <Text fw={600} size="sm">
      {label}
    </Text>
    {email && (
      <Text
        size="xs"
        c="dimmed"
        ta="center"
        style={{ wordBreak: "break-word" }}
      >
        {email}
      </Text>
    )}
  </Stack>
);

// Status badge + arrow pointing from Microsoft toward VAPAR. Top margin aligns
// the badge vertically with the framed logos on either side.
const Connector = ({ success }) => (
  <Group
    gap={4}
    wrap="nowrap"
    align="center"
    style={{ flexShrink: 0, marginTop: (LOGO_BOX - BADGE) / 2 }}
  >
    <ThemeIcon color={success ? "teal" : "red"} radius="xl" size={BADGE}>
      {success ? <IconCheck size={22} /> : <IconX size={22} />}
    </ThemeIcon>
    <IconArrowNarrowRight
      size={30}
      style={{
        flexShrink: 0,
        color: success
          ? "var(--mantine-color-teal-6)"
          : "var(--mantine-color-red-6)",
      }}
    />
  </Group>
);

// Result of a Microsoft-account link attempt, shown as a prominent, dismissable
// modal (rather than a toast) so the outcome is unmissable. Driven by
// AuthContext.msalLinkResult.
const MsLinkResultModal = () => {
  const { msalLinkResult, clearMsalLinkResult, linkMicrosoftAccount } =
    useContext(AuthContext);

  const result = msalLinkResult;
  const isSuccess = result?.status === "success";

  const handleRetry = async () => {
    clearMsalLinkResult();
    try {
      await linkMicrosoftAccount();
    } catch (error) {
      showErrorNotification({
        title: "Link Microsoft account",
        userMessage:
          error?.message ||
          "Could not start the Microsoft link flow. Please try again.",
      });
    }
  };

  return (
    <Modal
      opened={!!result}
      onClose={clearMsalLinkResult}
      centered
      size="md"
      withCloseButton
      data-testid="ms-link-result-modal"
    >
      {result && (
        <Stack align="center" gap="md" px="sm" pb="md">
          <Title order={4} ta="center">
            {result.title ||
              (isSuccess
                ? "Microsoft account linked"
                : "Couldn't link Microsoft account")}
          </Title>

          <Group
            align="flex-start"
            justify="center"
            gap="sm"
            wrap="nowrap"
            w="100%"
            pb="md"
          >
            <AccountBlock
              logo={<MicrosoftLogo size={28} />}
              label="Microsoft"
              email={result.msEmail}
            />
            <Connector success={isSuccess} />
            <AccountBlock
              logo={<img src={vaparLogo} alt="VAPAR" width={30} height={30} />}
              label="VAPAR"
              email={result.vaparEmail}
            />
          </Group>

          {/* Title and description both come from the server (one place for the
              copy). Description carries the details and the call to action. */}
          {result.description && (
            <Text size="sm" ta="center">
              {result.description}
            </Text>
          )}

          <Group justify="center" gap="sm" mt="xs">
            {!isSuccess && (
              <Button
                variant="default"
                leftSection={<MicrosoftLogo size={14} />}
                onClick={handleRetry}
                data-testid="ms-link-result-retry"
              >
                Try again
              </Button>
            )}
            <Button
              variant={isSuccess ? "filled" : "subtle"}
              onClick={clearMsalLinkResult}
              data-testid="ms-link-result-dismiss"
            >
              {isSuccess ? "Done" : "Dismiss"}
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
};

export default MsLinkResultModal;
